import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useDispatch, useSelector } from "react-redux";
import * as dropdown from "../dropdowns";
import { Bell, ChevronUp, Menu, ShoppingCart, X } from "lucide-react";
import { getCollectionDetail } from "../../redux/actions/collectionActions";

function MobileNav({ handleLogout, loggingOut }) {
  const [openMobileNavbar, setOpenMobileNavbar] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(""); // Tracks which single section is open
  const user = useSelector((state) => state.auth?.user);
  const cartItems = useSelector((state) => state.cart.cartItems || []);
  const cartCount = cartItems.length;
  const isAuthenticated = !!user;
  const dispatch = useDispatch();
  const { items, loading, error } = useSelector((state) => state.collection);
  const location = useLocation();

  // Toggle a given accordion section, or close it if it was already open
  const toggleAccordion = (section) => {
    setOpenAccordion(openAccordion === section ? "" : section);
  };

  const categoryRoutes = [
    "/collections/mens",
    "/collections/womens",
    "/collections/kids",
  ];
  const isCategoryActive = categoryRoutes.some((route) =>
    location.pathname.startsWith(route)
  );

  useEffect(() => {
    dispatch(getCollectionDetail("all"));
  }, []);

  useEffect(() => {
    if (openMobileNavbar) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [openMobileNavbar]);

  return (
    <nav className="lg:hidden">
      <div className="px-4 bg-theme-color">
        <div className="flex items-center gap-2">
          <div
            onClick={() => setOpenMobileNavbar(true)}
            className="cursor-pointer text-2xl hover:scale-110 transition-transform duration-200"
          >
            <Menu />
          </div>
          <a className="h-16" href={isAuthenticated ? "/home" : "/"}>
            <img loading="lazy" src={logo} alt="logo" className="size-full" />
          </a>
        </div>
      </div>

      <div
        className={`${
          openMobileNavbar ? "block translate-x-0 fixed" : "-translate-x-[100%]"
        } transition-transform duration-300 ease-in-out inset-0 fixed bg-white shadow-lg p-4 z-[60] flex flex-col gap-4`}
      >
        <div className="flex justify-between">
          <img loading="lazy" src={logo} alt="logo" className="pb-2 h-28" />

          <span
            className="cursor-pointer text-2xl hover:scale-110 transition-transform duration-200"
            onClick={() => setOpenMobileNavbar(false)}
          >
            <X />
          </span>
        </div>
        <ul className="navbar-nav gap-4">
          {/* Home Link */}
          <li onClick={() => setOpenMobileNavbar(false)}>
            <NavLink
              className={({ isActive }) =>
                `nav-link ${
                  isActive
                    ? "bg-normal-theme-color text-white rounded-3xl px-3 py-1"
                    : ""
                }`
              }
              onClick={() => setOpenMobileNavbar(false)}
              to={isAuthenticated ? "/home" : "/"}
            >
              Home
            </NavLink>
          </li>

          {/* Categories Accordion */}
          <li>
            <button
              className="w-full text-left nav-link flex justify-between items-center"
              onClick={() => toggleAccordion("categories")}
            >
              Categories
              <span>
                <ChevronUp
                  className={`${
                    openAccordion === "categories" ? "" : "rotate-180"
                  } transition-transform duration-200`}
                />
              </span>
            </button>
            {openAccordion === "categories" && (
              <div className="flex flex-col gap-3 pl-3">
                {items?.length > 0 &&
                  items.map((category) => (
                    <NavLink
                      onClick={() => setOpenMobileNavbar(false)}
                      key={category.cat_name}
                      to={`/collections/${category.cat_name}`}
                    >
                      <p className="text-sm text-gray-600 py-1 border-b">
                        {category.cat_name}
                      </p>
                    </NavLink>
                  ))}
              </div>
            )}
          </li>

          {/* Wishlist Link */}
          {isAuthenticated && (
            <li onClick={() => setOpenMobileNavbar(false)}>
              <NavLink
                className={({ isActive }) =>
                  `nav-link ${
                    isActive
                      ? "bg-normal-theme-color text-white rounded-3xl px-3 py-1"
                      : ""
                  }`
                }
                to="/wishlist"
              >
                Wishlist
              </NavLink>
            </li>
          )}

          {/* Notifications Link */}
          {isAuthenticated && (
            <li onClick={() => setOpenMobileNavbar(false)}>
              <NavLink
                className={({ isActive }) =>
                  `nav-link flex items-center ${
                    isActive
                      ? "bg-normal-theme-color text-white rounded-3xl px-3 py-1"
                      : ""
                  }`
                }
                to="/notifications"
              >
                {/* <Bell strokeWidth={2.75} className="size-5" /> */}

                <span>Notifications</span>
              </NavLink>
            </li>
          )}

          {/* Cart Link */}
          {isAuthenticated && (
            <li className="relative" onClick={() => setOpenMobileNavbar(false)}>
              <NavLink
                className={({ isActive }) =>
                  `nav-link flex items-center ${
                    isActive
                      ? "bg-normal-theme-color text-white rounded-3xl px-3 py-1"
                      : ""
                  }`
                }
                to="/cart"
              >
                {/* <ShoppingCart strokeWidth={2.75} className="size-5" /> */}

                {isAuthenticated && cartCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                    {cartCount}
                  </span>
                )}
                <span c>Cart</span>
              </NavLink>
            </li>
          )}

          {/* My Account (if logged in) OR Login + Become a Partner */}
          {isAuthenticated ? (
            <li>
              <button
                className="w-full text-left nav-link flex justify-between items-center"
                onClick={() => toggleAccordion("account")}
              >
                My Account
                <span>
                  <ChevronUp
                    className={`${
                      openAccordion === "account" ? "" : "rotate-180"
                    } transition-transform duration-200`}
                  />
                </span>
              </button>
              {openAccordion === "account" && (
                <div className="flex flex-col gap-3 pl-3">
                  {/* Flattened My Account Links */}
                  <NavLink
                    onClick={() => setOpenMobileNavbar(false)}
                    to="/orders"
                  >
                    <p className="text-sm text-gray-600 py-1 border-b">
                      Orders
                    </p>
                  </NavLink>
                  <NavLink
                    onClick={() => setOpenMobileNavbar(false)}
                    to="/profile"
                  >
                    <p className="text-sm text-gray-600 py-1 border-b">
                      Profile
                    </p>
                  </NavLink>
                  <NavLink
                    onClick={() => setOpenMobileNavbar(false)}
                    to="/wishlist"
                  >
                    <p className="text-sm text-gray-600 py-1 border-b">
                      Wishlist
                    </p>
                  </NavLink>
                  <button
                    onClick={() => {
                      handleLogout();
                      setOpenMobileNavbar(false);
                    }}
                    disabled={loggingOut}
                    className="text-sm text-gray-600 py-1 border-b text-left"
                  >
                    {loggingOut ? "Logging Out…" : "Logout"}
                  </button>
                </div>
              )}
            </li>
          ) : (
            <div className="flex flex-col md:flex-row gap-4">
              {/* Login Link */}
              <NavLink
                className={({ isActive }) =>
                  `px-3 py-2 border hover:border-normal-theme-color rounded-lg outline-none ${
                    isActive &&
                    "bg-normal-theme-color text-white rounded-3xl px-3 py-1"
                  }`
                }
                onClick={() => setOpenMobileNavbar(false)}
                to="/login"
              >
                <li className="">Login</li>
              </NavLink>

              {/* Become a Partner Accordion */}
              <li onClick={() => setOpenMobileNavbar(false)}>
                <NavLink to="/vendor-registration">
                  <button className="w-full bg-normal-theme-color text-white px-3 py-2 rounded-lg text-left nav-link flex justify-between items-center">
                    Become a Partner
                  </button>
                </NavLink>
              </li>
            </div>
          )}
        </ul>
      </div>
    </nav>
  );
}

export default MobileNav;
